import { Component } from '@theme/component';

/**
 * Announcement banner: fades between currently visible (PC/SP) slides.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {MediaQueryList} */
  #desktopQuery = window.matchMedia('(min-width: 750px)');

  /** @type {MediaQueryList} */
  #hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    super.connectedCallback();

    if (this.#hoverQuery.matches) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
    }

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktopQuery.addEventListener('change', this.#handleViewportChange);

    this.#sync();
    this.play();
  }

  disconnectedCallback() {
    this.#desktopQuery.removeEventListener('change', this.#handleViewportChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    super.disconnectedCallback();
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getAllSlides() {
    const fromRefs = this.refs.slides;
    if (Array.isArray(fromRefs) && fromRefs.length > 0) {
      return /** @type {HTMLElement[]} */ (fromRefs);
    }

    return Array.from(this.querySelectorAll('.announcement-bar__slide'));
  }

  /**
   * Slides actually shown in the current viewport (honours hidden--desktop / hidden--mobile).
   * @returns {HTMLElement[]}
   */
  #getVisibleSlides() {
    return this.#getAllSlides().filter((slide) => window.getComputedStyle(slide).display !== 'none');
  }

  #sync() {
    const visibleSlides = this.#getVisibleSlides();
    const allSlides = this.#getAllSlides();
    const canSlide = visibleSlides.length > 1;

    this.classList.toggle('is-sliding', canSlide);
    this.classList.add('is-ready');

    if (visibleSlides.length === 0) {
      allSlides.forEach((slide) => slide.setAttribute('aria-hidden', 'true'));
      this.suspend();
      return;
    }

    let relativeIndex = this.#current % visibleSlides.length;
    if (relativeIndex < 0) relativeIndex += visibleSlides.length;

    allSlides.forEach((slide) => {
      if (!visibleSlides.includes(slide)) {
        slide.setAttribute('aria-hidden', 'true');
        return;
      }

      const visibleIndex = visibleSlides.indexOf(slide);
      slide.setAttribute('aria-hidden', visibleIndex === relativeIndex ? 'false' : 'true');
    });

    if (!canSlide) {
      this.suspend();
      return;
    }

    if (this.autoplay && !this.paused && this.#interval === undefined) {
      this.play();
    }
  }

  #handleViewportChange = () => {
    this.#current = 0;
    this.suspend();
    this.#sync();
    if (!this.paused) this.play();
  };

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault?.();
    if (this.#getVisibleSlides().length < 2) return;
    this.current += 1;
  }

  /** @param {Event} [event] */
  previous(event) {
    event?.preventDefault?.();
    if (this.#getVisibleSlides().length < 2) return;
    this.current -= 1;
  }

  /** @param {number} [interval] */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#getVisibleSlides().length < 2) return;

    this.suspend();
    this.paused = false;

    this.#interval = setInterval(() => {
      if (document.hidden) return;
      if (this.#hoverQuery.matches && this.matches(':hover')) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.paused = false;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;
    this.#sync();
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
